import { useState, useMemo, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  TrendingUp,
  Phone,
  AlertTriangle,
  Loader2,
  RefreshCw,
  Wifi,
  WifiOff,
  ChevronDown,
  History,
  LineChart,
  Settings2,
} from 'lucide-react';
import { FundSelector } from './FundSelector';
import { TimeRangeSelector, TimeRangeMode, HistoricalRange, ForecastRange } from './TimeRangeSelector';
import { StressTestSlider } from './StressTestSlider';
import { PortfolioComparisonChart } from './PortfolioComparisonChart';
import { FUND_OPTIONS, calculateMetrics } from '@/lib/mock-market-data';
import { useMarketData } from '@/hooks/useMarketData';
import { cn } from '@/lib/utils';

// Metrics interface for Header display
export interface ModelMetrics {
  userPortfolio: { value: number; totalReturn: number };
  benchmark: { value: number; totalReturn: number };
  tactical: { value: number; totalReturn: number; name: string };
}

interface ModelTabProps {
  portfolioValue?: number;
  portfolioExpectedReturn?: number; // Actual portfolio expected annual return (e.g., 0.07 for 7%)
  portfolioVolatility?: number; // Actual portfolio volatility (e.g., 0.15 for 15%)
  onMetricsChange?: (metrics: ModelMetrics | null) => void;
}

export function ModelTab({
  portfolioValue = 100000,
  portfolioExpectedReturn = 0.07, // Default to 7% if not provided
  portfolioVolatility = 0.15, // Default to 15% if not provided
  onMetricsChange
}: ModelTabProps) {
  // State
  const [selectedFund, setSelectedFund] = useState('TACTICAL');
  const [mode, setMode] = useState<TimeRangeMode>('historical');
  const [historicalRange, setHistoricalRange] = useState<HistoricalRange>('5');
  const [forecastRange, setForecastRange] = useState<ForecastRange>('10');
  const [stressLevel, setStressLevel] = useState(0);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Determine if tactical fund is selected
  const isTactical = selectedFund === 'TACTICAL';

  // Get fund name for display
  const fundName = useMemo(() => {
    const allFunds = Object.values(FUND_OPTIONS).flat();
    const fund = allFunds.find(f => f.ticker === selectedFund);
    return fund?.name ?? selectedFund;
  }, [selectedFund]);

  // Fetch market data using the hook
  const years = mode === 'historical' ? parseInt(historicalRange) : parseInt(forecastRange);
  const { data: chartData, isLoading, error, isRealData, refetch } = useMarketData({
    selectedFund,
    years,
    portfolioValue,
    portfolioExpectedReturn,
    portfolioVolatility,
    mode,
    stressLevel,
  });

  // Calculate and report metrics to parent when chart data changes
  useEffect(() => {
    if (chartData && onMetricsChange) {
      const userMetrics = calculateMetrics(chartData.userPortfolio);
      const benchmarkMetrics = calculateMetrics(chartData.benchmark);
      const tacticalMetrics = calculateMetrics(chartData.tacticalFund);

      onMetricsChange({
        userPortfolio: { value: userMetrics.finalValue, totalReturn: userMetrics.totalReturn },
        benchmark: { value: benchmarkMetrics.finalValue, totalReturn: benchmarkMetrics.totalReturn },
        tactical: { value: tacticalMetrics.finalValue, totalReturn: tacticalMetrics.totalReturn, name: fundName },
      });
    } else if (!chartData && onMetricsChange) {
      onMetricsChange(null);
    }
  }, [chartData, fundName, onMetricsChange]);

  return (
    <div className="flex flex-col -mx-3 sm:-mx-4 -mb-4 sm:-mb-6">
      {/* Full Viewport Chart Section - fills remaining viewport */}
      <div className="relative h-[calc(100vh-12rem)] min-h-[400px] flex">
        {/* Left Sidebar Control Panel */}
        <div
          className={cn(
            'absolute left-0 top-0 bottom-0 z-[5] transition-all duration-300 ease-in-out',
            'bg-background/95 backdrop-blur-sm border-r shadow-lg',
            sidebarCollapsed ? 'w-12' : 'w-80'
          )}
        >
          {/* Sidebar Header */}
          <div className="flex items-center justify-between p-3 border-b">
            {!sidebarCollapsed && (
              <div className="flex items-center gap-2">
                <Settings2 className="h-4 w-4 text-primary" />
                <span className="font-semibold text-sm">Controls</span>
              </div>
            )}
            <Button
              variant="ghost"
              size="sm"
              className={cn('h-8 w-8 p-0', sidebarCollapsed && 'mx-auto')}
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            >
              <ChevronDown
                className={cn(
                  'h-4 w-4 transition-transform',
                  sidebarCollapsed ? '-rotate-90' : 'rotate-90'
                )}
              />
            </Button>
          </div>

          {/* Sidebar Content */}
          {!sidebarCollapsed && (
            <div className="p-4 space-y-6 overflow-y-auto h-[calc(100%-3.5rem)]">
              {/* Data Source Badge */}
              <div className="flex items-center gap-2">
                {isLoading ? (
                  <Badge variant="secondary" className="gap-1">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Loading
                  </Badge>
                ) : mode === 'historical' ? (
                  <Badge
                    variant={isRealData ? 'default' : 'secondary'}
                    className={cn('gap-1', isRealData && 'bg-status-good')}
                  >
                    {isRealData ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
                    {isRealData ? 'Live Data' : 'Mock Data'}
                  </Badge>
                ) : (
                  <Badge variant="outline" className="gap-1">
                    <LineChart className="h-3 w-3" />
                    Projection
                  </Badge>
                )}
              </div>

              {/* Mode Selection */}
              <div className="space-y-3">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  View Mode
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant={mode === 'historical' ? 'default' : 'outline'}
                    size="sm"
                    className="gap-1.5"
                    onClick={() => setMode('historical')}
                  >
                    <History className="h-3.5 w-3.5" />
                    Historical
                  </Button>
                  <Button
                    variant={mode === 'forecast' ? 'default' : 'outline'}
                    size="sm"
                    className="gap-1.5"
                    onClick={() => setMode('forecast')}
                  >
                    <TrendingUp className="h-3.5 w-3.5" />
                    Forecast
                  </Button>
                </div>
              </div>

              <Separator />

              {/* Time Range */}
              <div className="space-y-3">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Time Range
                </label>
                <TimeRangeSelector
                  mode={mode}
                  onModeChange={setMode}
                  historicalRange={historicalRange}
                  onHistoricalRangeChange={setHistoricalRange}
                  forecastRange={forecastRange}
                  onForecastRangeChange={setForecastRange}
                  compact
                />
              </div>

              <Separator />

              {/* Fund Selector */}
              <div className="space-y-3">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Compare Against
                </label>
                <FundSelector value={selectedFund} onChange={setSelectedFund} compact />
              </div>

              {/* Stress Test (only in forecast mode) */}
              {mode === 'forecast' && (
                <>
                  <Separator />
                  <div className="space-y-3">
                    <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      Stress Scenario
                    </label>
                    <StressTestSlider
                      value={stressLevel}
                      onChange={setStressLevel}
                      compact
                    />
                  </div>
                </>
              )}

              <Separator />

              {/* Legend / Info */}
              <div className="space-y-2 text-xs text-muted-foreground">
                <p className="font-medium text-foreground">Chart Legend</p>
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-0.5 bg-primary rounded" />
                    <span>Your Portfolio</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-0.5 bg-muted-foreground rounded opacity-60" style={{ backgroundImage: 'repeating-linear-gradient(90deg, currentColor, currentColor 2px, transparent 2px, transparent 4px)' }} />
                    <span>S&P 500</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className={cn('w-3 h-0.5 rounded', isTactical ? 'bg-status-good' : 'bg-status-warning')} />
                    <span className="truncate">{fundName}</span>
                  </div>
                </div>
              </div>

              {/* Compliance Note */}
              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="text-[10px] text-muted-foreground leading-relaxed">
                  {mode === 'historical' ? (
                    <>
                      <strong>Your Portfolio</strong> shows a hypothetical backtest.{' '}
                      Past performance does not guarantee future results.
                    </>
                  ) : (
                    <>
                      All projections are hypothetical and for educational purposes only.
                      {stressLevel < -10 && <strong className="text-status-warning"> Stress scenario applied.</strong>}
                    </>
                  )}
                </p>
              </div>
            </div>
          )}

          {/* Collapsed Icons */}
          {sidebarCollapsed && (
            <div className="flex flex-col items-center gap-4 p-2 pt-4">
              <Button
                variant={mode === 'historical' ? 'default' : 'ghost'}
                size="icon"
                className="h-8 w-8"
                onClick={() => setMode('historical')}
                title="Historical"
              >
                <History className="h-4 w-4" />
              </Button>
              <Button
                variant={mode === 'forecast' ? 'default' : 'ghost'}
                size="icon"
                className="h-8 w-8"
                onClick={() => setMode('forecast')}
                title="Forecast"
              >
                <TrendingUp className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>

        {/* Chart Area */}
        <div
          className={cn(
            'flex-1 transition-all duration-300',
            sidebarCollapsed ? 'ml-12' : 'ml-80'
          )}
        >
          {isLoading ? (
            <div className="h-full flex items-center justify-center bg-muted/10">
              <div className="text-center space-y-3">
                <Loader2 className="h-12 w-12 animate-spin mx-auto text-primary" />
                <p className="text-sm text-muted-foreground">
                  Fetching market data...
                </p>
              </div>
            </div>
          ) : error && !chartData ? (
            <div className="h-full flex items-center justify-center bg-destructive/5">
              <div className="text-center space-y-3">
                <AlertTriangle className="h-12 w-12 mx-auto text-destructive" />
                <p className="text-sm text-destructive font-medium">
                  Failed to load market data
                </p>
                <p className="text-xs text-muted-foreground max-w-xs">
                  {error}
                </p>
                <Button variant="outline" size="sm" onClick={refetch} className="gap-2">
                  <RefreshCw className="h-4 w-4" />
                  Try Again
                </Button>
              </div>
            </div>
          ) : chartData ? (
            <PortfolioComparisonChart
              key={`${mode}-${years}-${stressLevel}-${selectedFund}`}
              data={chartData}
              fundName={fundName}
              isTactical={isTactical}
              isHistorical={mode === 'historical'}
              fullscreen
            />
          ) : null}
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 animate-bounce">
          <ChevronDown className="h-6 w-6 text-muted-foreground/50" />
        </div>
      </div>

      {/* Below-fold Content */}
      <div className="container mx-auto px-4 py-8 space-y-6">
        {/* Metrics Cards */}
        {chartData && !isLoading && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <MetricCard
              title="Your Portfolio"
              data={chartData.userPortfolio}
              color="primary"
              badge={mode === 'historical' ? 'Hypothetical' : 'Projected'}
            />
            <MetricCard
              title="S&P 500"
              data={chartData.benchmark}
              color="muted"
              badge="Benchmark"
            />
            <MetricCard
              title={fundName}
              data={chartData.tacticalFund}
              color={isTactical ? 'good' : 'warning'}
              badge={isTactical ? 'Tactical' : 'Index'}
            />
          </div>
        )}

        {/* CTA Section */}
        <Card className={cn(
          'border-2 transition-colors',
          isTactical ? 'border-primary/50 bg-primary/5' : 'border-muted'
        )}>
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="space-y-1">
                <h3 className="font-semibold">
                  {isTactical
                    ? 'Interested in tactical portfolio management?'
                    : 'Want to optimize your portfolio?'}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {isTactical
                    ? 'Learn how our tactical strategy helps protect against market downturns while capturing upside.'
                    : 'Talk to a professional about strategies that may improve your risk-adjusted returns.'}
                </p>
              </div>
              <Button size="lg" className="gap-2 shrink-0">
                <Phone className="h-4 w-4" />
                Schedule a Call
              </Button>
            </div>

            {/* Disclaimer */}
            <p className="text-[10px] text-muted-foreground mt-4 leading-relaxed">
              The projections shown are hypothetical and for illustrative purposes only. They do not
              represent actual investment results and are not guarantees of future performance.
              Investment advisory services are provided by [Firm Name], a registered investment adviser.
              Past performance is not indicative of future results. All investments involve risk,
              including loss of principal.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Metric Card Component
interface MetricCardProps {
  title: string;
  data: { date: string; value: number }[];
  color: 'primary' | 'muted' | 'good' | 'warning';
  badge: string;
}

function MetricCard({ title, data, color, badge }: MetricCardProps) {
  const startValue = data[0]?.value ?? 100000;
  const endValue = data[data.length - 1]?.value ?? 100000;
  const totalReturn = (endValue - startValue) / startValue;
  const years = data.length / 252; // Approximate trading days per year
  const cagr = Math.pow(endValue / startValue, 1 / years) - 1;

  // Calculate max drawdown
  let peak = startValue;
  let maxDrawdown = 0;
  for (const point of data) {
    if (point.value > peak) peak = point.value;
    const drawdown = (peak - point.value) / peak;
    if (drawdown > maxDrawdown) maxDrawdown = drawdown;
  }

  const formatCurrency = (value: number) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(2)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value.toFixed(0)}`;
  };

  const formatPercent = (value: number) => {
    const sign = value >= 0 ? '+' : '';
    return `${sign}${(value * 100).toFixed(1)}%`;
  };

  const colorClasses = {
    primary: 'border-primary/30',
    muted: 'border-muted',
    good: 'border-status-good/30',
    warning: 'border-status-warning/30',
  };

  return (
    <Card className={colorClasses[color]}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium truncate">{title}</span>
          <Badge variant="outline" className="text-xs shrink-0">
            {badge}
          </Badge>
        </div>
        <div className="space-y-1">
          <p className="text-2xl font-mono font-bold">
            {formatCurrency(endValue)}
          </p>
          <div className="flex items-center gap-2 text-sm">
            <span className={cn(
              totalReturn >= 0 ? 'text-status-good' : 'text-status-critical'
            )}>
              {formatPercent(totalReturn)} total
            </span>
            <span className="text-muted-foreground">|</span>
            <span className="text-muted-foreground">
              {formatPercent(cagr)} CAGR
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            Max drawdown: {formatPercent(-maxDrawdown)}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
